import { z } from 'zod';
import { TenantIdSchema, EmailSchema, PhoneSchema, IsoDateSchema } from './_primitives.js';

export const CARRIER_STATUS = Object.freeze({
  ACTIVE:    'active',
  INACTIVE:  'inactive',
  PENDING:   'pending',
  SUSPENDED: 'suspended',
});

export const CarrierStatusEnum = z.enum(Object.values(CARRIER_STATUS));

// Carrier AP (accounts-payable) sub-flow status — TMS-V2 load lifecycle.
// Consumed by src/modules/tms/services/LoadDataService.js (CARRIER_PAYMENT_EVENT
// topic map + auto-close on CARRIER_PAID). Values are the snake_case forms
// already used as record values across the codebase. Restored here because
// LoadDataService imported CARRIER_PAYMENT_STATUS from @kairos/types but the
// definition was never added, breaking the production build on main
// (TMS-TYPES-CARRIER-PAYMENT-STATUS-HOTFIX-1).
export const CARRIER_PAYMENT_STATUS = Object.freeze({
  CARRIER_BILL_READY:       'carrier_bill_ready',
  CARRIER_INVOICE_RECEIVED: 'carrier_invoice_received',
  CARRIER_AUDIT:            'carrier_audit',
  CARRIER_APPROVED:         'carrier_approved',
  CARRIER_PAID:             'carrier_paid',
});

export const CarrierPaymentStatusEnum = z.enum(Object.values(CARRIER_PAYMENT_STATUS));

export const CarrierSchema = z.object({
  tenantId:    TenantIdSchema,
  id:          z.string().optional(),
  name:        z.string().min(1),
  scac:        z.string().max(4).optional(),
  dotNumber:   z.string().optional(),
  mcNumber:    z.string().optional(),
  status:      CarrierStatusEnum.optional(),
  email:       EmailSchema.optional(),
  phone:       PhoneSchema.optional(),
  address:     z.string().optional(),
  rating:          z.number().min(0).max(5).optional(),
  preferredModes:  z.array(z.string()).optional(),
  // KSFD vetting signals (TMS-CARRIER-VETTING-SURFACE-1)
  complianceScore:  z.number().min(0).max(100).optional(),
  safetyRating:     z.number().min(0).max(5).optional(),
  capacityAvailable: z.number().nonnegative().optional(),
  slaCompliancePct:  z.number().min(0).max(100).optional(),
  vettedAt:          z.union([IsoDateSchema, z.object({})]).optional(),
  insuranceExpiry: z.string().optional(),
  isDeleted:   z.boolean().optional(),
  createdAt:   z.union([z.string(), z.object({})]).optional(),
  updatedAt:   z.union([z.string(), z.object({})]).optional(),
  version:     z.number().int().optional(),
}).passthrough();

/** @typedef {import('zod').infer<typeof CarrierSchema>} Carrier */
